import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import Header from "../../components/Header/Header";
import FilterBar from "../../components/FilterBar/FilterBar";
import MetricCard from "../../components/MetricCard/MetricCard";
import PostCard from "../../components/PostCard/PostCard";
import Modal from "../../components/Modal/Modal";
import PlatformTable from "../../components/PlatformTable/PlatformTable";
import ExportCard from "../../components/ExportCard/ExportCard";
import BottomNav from "../../components/BottomNav/BottomNav";
import { useNavigate } from "react-router-dom";
import "./DashboardPage.css";

const DashboardPage = () => {
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [allData, setAllData] = useState([]);
  const [filteredPostsArray, setFilteredPostsArray] = useState([]);
  const [filteredPlatformData, setFilteredPlatformData] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const navigate = useNavigate();

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "dashboard") navigate("/dashboard");
    else if (tabId === "analytics") navigate("/analytics");
    else if (tabId === "profile") navigate("/profile");
  };

  // Load Excel
  useEffect(() => {
    fetch("../data/posts_data (2).xlsx")
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

        const processedSheet = sheet.map((row) => {
          let parsedDate;
          if (typeof row.Date === "number") {
            const excelDate = XLSX.SSF.parse_date_code(row.Date);
            parsedDate = new Date(excelDate.y, excelDate.m - 1, excelDate.d);
          } else if (typeof row.Date === "string") {
            const parts = row.Date.split("/");
            if (parts.length === 3)
              parsedDate = new Date(+parts[2], parts[1] - 1, +parts[0]);
            else parsedDate = new Date(row.Date);
          }

          // Each row is already post-channel level
          return { ...row, Date: parsedDate };
        });

        setAllData(processedSheet);
      })
      .catch((err) => console.error("Error loading Excel file:", err));
  }, []);

  // Filter & group posts
  useEffect(() => {
    if (!allData.length) return;

    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const filteredData = allData.filter((row) => {
      const inLast30Days = row.Date instanceof Date && row.Date >= thirtyDaysAgo && row.Date <= today;
      const matchesPlatform = selectedPlatform ? row.Platform === selectedPlatform : true;
      return inLast30Days && matchesPlatform;
    });

// Group posts by date
const groupedPosts = filteredData.reduce((acc, row) => {
  const dateKey = row.Date ? row.Date.toLocaleDateString("en-GB") : "Unknown";
  if (!acc[dateKey]) acc[dateKey] = [];
  acc[dateKey].push(row);
  return acc;
}, {});

const postsArray = Object.entries(groupedPosts).map(([date, posts]) => ({
  date,
  posts,
  metrics: {
    posts: [...new Set(posts.map(p => p.Posts))].length, // ✅ count unique post titles only
    views: posts.reduce((acc, r) => acc + (r.Views || 0), 0),
    clicks: posts.reduce((acc, r) => acc + (r.Clicks || 0), 0),
  },
    })).sort((a, b) => {
      const [dayA, monthA, yearA] = a.date.split("/").map(Number);
      const [dayB, monthB, yearB] = b.date.split("/").map(Number);
      return new Date(yearB, monthB - 1, dayB) - new Date(yearA, monthA - 1, dayA);
    });

    // Platform summary
    const platformData = filteredData.reduce((acc, row) => {
      const plat = row.Platform || "Unknown";
      if (!acc[plat]) acc[plat] = { platform: plat, posts: 0, views: 0, clicks: 0 };
      acc[plat].posts += 1;
      acc[plat].views += row.Views || 0;
      acc[plat].clicks += row.Clicks || 0;
      return acc;
    }, {});

    setFilteredPostsArray(postsArray);
    setFilteredPlatformData(Object.values(platformData));
  }, [allData, selectedPlatform]);

  // Metrics
  const totalViews = filteredPostsArray.reduce((acc, p) => acc + p.metrics.views, 0);
  const totalClicks = filteredPostsArray.reduce((acc, p) => acc + p.metrics.clicks, 0);
  const totalTransactions = filteredPostsArray.reduce(
    (acc, p) => acc + p.posts.reduce((sum, row) => sum + (row.Transactions || 0), 0),
    0
  );
  const totalROAS = filteredPostsArray.reduce(
    (acc, p) => acc + p.posts.reduce((sum, row) => sum + (row.ROAS || 0), 0),
    0
  ) / (filteredPostsArray.flatMap(p => p.posts).length || 1);

  const metrics = [
    { title: "Total Views", value: totalViews },
    { title: "Total Clicks", value: totalClicks },
    { title: "Total Transactions", value: totalTransactions },
    { title: "Avg ROAS", value: totalROAS.toFixed(2) + "x" },
  ];

  return (
    <div className="phone-frame">
      <div className="dashboard-container">
        <Header username="ERetailer" />

        <div className="dashboard-scroll">
          <FilterBar
            selectedPlatform={selectedPlatform}
            onPlatformChange={setSelectedPlatform}
          />

          {/* Metrics */}
          <div className="metrics-grid">
            {metrics.map((m, i) => (
              <MetricCard key={i} title={m.title} value={m.value} />
            ))}
          </div>

          {/* Posts Section */}
          <div className="posts-container">
            <div className="posts-header">Posts</div>
            <div className="posts-body">
              {filteredPostsArray.map((p, i) => (
                <PostCard
                  key={i}
                  date={p.date}
                  metrics={p.metrics}
                  onClick={() => setSelectedPost(p)}
                />
              ))}

              <Modal
                isOpen={!!selectedPost}
                onClose={() => setSelectedPost(null)}
                postData={selectedPost}
              />
            </div>
          </div>

          {/* Platform summary */}
          <PlatformTable data={filteredPlatformData} />

          {/* Export */}
          <ExportCard data={filteredPostsArray.flatMap(p => p.posts)} />
        </div>

        <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
      </div>
    </div>
  );
};

export default DashboardPage;
